import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Search } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  promotionBundlesApi,
  type PromotionBundle,
  type PromotionBundleSavePayload,
} from '@/api/promotion-bundles'
import { showError } from '@/lib/error'
import { PromotionBundleDialog } from './components/PromotionBundleDialog'
import { PromotionBundleTable } from './components/PromotionBundleTable'

export function PromotionBundlesPage() {
  const queryClient = useQueryClient()
  const [keyword, setKeyword] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [status, setStatus] = useState('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [editing, setEditing] = useState<PromotionBundle | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['promotion-bundles', searchTerm, status, page, pageSize],
    queryFn: () => promotionBundlesApi.list({
      keyword: searchTerm,
      status: status === 'all' ? '' : status,
      page,
      page_size: pageSize,
    }),
  })

  const saveMutation = useMutation({
    mutationFn: (payload: PromotionBundleSavePayload) => (
      editing ? promotionBundlesApi.update(editing.id, payload) : promotionBundlesApi.create(payload)
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotion-bundles'] })
      setDialogOpen(false)
      setEditing(null)
      toast.success('促销组合已保存')
    },
    onError: (error) => showError(error, '保存失败'),
  })

  const deleteMutation = useMutation({
    mutationFn: (bundle: PromotionBundle) => promotionBundlesApi.delete(bundle.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotion-bundles'] })
      toast.success('促销组合已删除')
    },
    onError: (error) => showError(error, '删除失败'),
  })

  const handleSearch = () => {
    setSearchTerm(keyword.trim())
    setPage(1)
  }

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize)
    setPage(1)
  }

  const handleOpenCreate = () => {
    setEditing(null)
    setDialogOpen(true)
  }

  const handleOpenEdit = (bundle: PromotionBundle) => {
    setEditing(bundle)
    setDialogOpen(true)
  }

  const handleDelete = (bundle: PromotionBundle) => {
    if (!window.confirm(`确定删除促销组合「${bundle.name}」吗？`)) return
    deleteMutation.mutate(bundle)
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">促销组合</h1>
          <p className="mt-1 text-sm text-muted-foreground">配置可用于手动创建订单的 SKU 组合价格</p>
        </div>
        <Button type="button" onClick={handleOpenCreate}>
          <Plus className="mr-2 h-4 w-4" />
          新建组合
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-background p-3">
        <div className="relative w-80 max-w-full">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleSearch()
            }}
            placeholder="搜索组合名称"
            className="pl-9"
          />
        </div>
        <Button type="button" variant="outline" onClick={handleSearch}>
          搜索
        </Button>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部状态</SelectItem>
            <SelectItem value="enabled">启用</SelectItem>
            <SelectItem value="disabled">停用</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="rounded-lg border py-12 text-center text-sm text-muted-foreground">加载中...</div>
      ) : (
        <PromotionBundleTable
          bundles={data?.list ?? []}
          onEdit={handleOpenEdit}
          onDelete={handleDelete}
        />
      )}

      <ListPagination
        page={page}
        pageSize={pageSize}
        total={data?.total || 0}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
      />

      <PromotionBundleDialog
        open={dialogOpen}
        bundle={editing}
        isSaving={saveMutation.isPending}
        onOpenChange={(open) => {
          setDialogOpen(open)
          if (!open) setEditing(null)
        }}
        onSubmit={(payload) => saveMutation.mutate(payload)}
      />
    </div>
  )
}
